import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Stack,
  Paper,
  IconButton,
  alpha,
  useTheme,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";

const AddColumnPanel = ({ onAdd, embedded = false }) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const columnWidth = embedded ? 260 : 272;

  const columnSurface = embedded
    ? theme.palette.background.paper
    : alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.35 : 0.72);

  const submit = async () => {
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    try {
      const ok = await onAdd(name.trim());
      if (ok !== false) {
        setName("");
        setOpen(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = () => {
    setName("");
    setOpen(false);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: columnWidth,
        minWidth: columnWidth,
        maxWidth: columnWidth,
        flex: "0 0 auto",
        alignSelf: "stretch",
        bgcolor: open ? columnSurface : alpha(columnSurface, 0.65),
        borderRadius: embedded ? 2 : 2.5,
        p: embedded ? 1 : 1.25,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        minHeight: embedded ? 280 : undefined,
        border: `1px dashed ${alpha(theme.palette.divider, open ? 0.2 : 0.45)}`,
        transition: "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
        boxShadow: open
          ? embedded
            ? `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}, inset 0 0 0 1px ${alpha(theme.palette.divider, 0.12)}`
            : theme.palette.mode === "dark"
              ? `inset 0 0 0 1px ${alpha(theme.palette.divider, 0.35)}`
              : `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`
          : "none",
        "&:hover": open
          ? undefined
          : {
              bgcolor: columnSurface,
              borderColor: alpha(theme.palette.primary.main, 0.35),
            },
      }}
    >
      {open ? (
        <Stack spacing={1.5}>
          <TextField
            size="small"
            placeholder={t("columnNamePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
            disabled={submitting}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
              if (e.key === "Escape") cancel();
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "background.paper",
              },
            }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              size="small"
              onClick={submit}
              disabled={!name.trim() || submitting}
              startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ fontWeight: 600, borderRadius: 1.5 }}
            >
              {submitting ? t("addingColumn") : t("addColumn")}
            </Button>
            <IconButton size="small" onClick={cancel} disabled={submitting} aria-label={t("cancel")}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      ) : (
        <Button
          fullWidth
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
          sx={{
            justifyContent: "flex-start",
            color: "text.secondary",
            fontWeight: 600,
            py: 1,
            borderRadius: 1.5,
            transition: "color 0.15s ease, background-color 0.15s ease",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              color: "primary.main",
            },
          }}
        >
          {t("addColumn")}
        </Button>
      )}
    </Paper>
  );
};

export default AddColumnPanel;
